import { DateTime } from 'luxon';

export enum JobStatus {
    PENDING = 'PENDING',
    AVAILABLE = 'AVAILABLE',
    ACCEPTED = 'ACCEPTED',
    CURRENT = 'CURRENT',
    ARRIVED = 'ARRIVED',
    COMPLETED = 'COMPLETED',
    HCP_ABORTED = 'HCP_ABORTED',
    WITHDRAWN = 'WITHDRAWN',
    CONTROLLER_ABORTED = 'CONTROLLER_ABORTED',
}

export enum JobType {
    UCR = 'ucr',
    VACCINATION = 'vaccination',
    ROUTE = 'route',
    HOME_VISIT = 'homeVisit',
    TELEPHONE = 'telephone',
    TELEPHONE_BLOCK = 'telephoneBlock',
}

export enum Flags {
    CARE_HOME = 'Care Home',
    CARER = 'Carer',
    STAFF = 'Staff',
    SHELTERED_HOUSING = 'Sheltered Housing',
    SUPPORTED_LIVING = 'Supported Living',
    REFUGEE = 'Refugee',
    TEST = 'Test',
    LEARNING_DIFFICULTY = 'Learning difficulty',
    NOK = 'NOK',
    HOUSEBOUND = 'Housebound',
}

export const friendlyStatuses = {
    PENDING: 'Pending',
    AVAILABLE: 'Available',
    ACCEPTED: 'Accepted',
    CURRENT: 'Current',
    ARRIVED: 'Arrived',
    COMPLETED: 'Completed',
    HCP_ABORTED: 'HCP aborted',
    WITHDRAWN: 'Withdrawn',
    CONTROLLER_ABORTED: 'Controller aborted',
};

export enum Pathways {
    RAPIDS = 'rapids',
    CADS = 'cads',
    D2A = 'd2a',
    VIRTUAL_WARD = 'virtualWard',
    UCR = 'ucr',
}

export interface IJob {
    createDateTime: string;
    createdBy?: string;
    lastUpdatedDateTime?: string;
    id: string;
    jobType: JobType | string;
    jobStatus: JobStatus;
    version: number;
    lastUpdatedBy: string;
    acceptedDateTime?: string;
    arrivedDateTime?: string | null;
    finishedDateTime?: string | null;
    hcpAbortedNotes?: string;
    hcpAbortedReason?: string;
    hcpId?: string | null;
    hcpName?: string | null;
    buddyId?: string | null;
    staffRequired?: number;
    madeCurrentDateTime?: string | null;
    nominatedHcps?: string[];
    controllerAbortedNotes?: string;
    controllerAbortedReason?: string;
    duration?: string;
    endDateTime?: string | null;
    flags?: Flags[];
    notes?: string;
    practice?: string;
    odsCode?: string;
    dateOfVisit?: string;
    startDateTime?: string | null;
    systmOneRef?: string;
    withdrawnReason?: string;
    withdrawnNotes?: string;
    hcpType?: string[];
    hcpGrade?: string[];
    buddyHcpType?: string[];
    buddyGrade?: string[];
    buddyName?: string | null;
    buddyJobStatus?: JobStatus;
    buddyMadeCurrentDateTime?: string | null;
    buddyArrivedDateTime?: string | null;
    buddyFinishedDateTime?: string | null;
    buddyHcpAbortedDateTime?: string | null;
    postCareNotes?: string;
    buddyHcpAbortedReason?: string;
    buddyHcpAbortedNotes?: string;
    buddyControllerAbortedReason?: string;
    buddyControllerAbortedNotes?: string;
    reverseAbortReason?: string;
    reverseAbortNotes?: string;
    jobStatusBeforeAbort?: JobStatus | null;
    buddyJobStatusBeforeAbort?: JobStatus | null;
    controllerReverseNotes?: string;
    controllerReverseReason?: string;
    hcpReverseNotes?: string;
    hcpReverseReason?: string;
    buddyControllerReverseReason?: string;
    buddyControllerReverseNotes?: string;
    buddyHcpReverseReason?: string;
    buddyHcpReverseNotes?: string;
    abortedBy?: string;
    buddyAbortedBy?: string;
    hcpAbortedDateTime?: string | null;
    postVisitNotes?: string;
    postVisitNotesBuddy?: string;
    markedCompletedByController?: boolean | null;
    markedCompletedByControllerNotes?: string;
    markedCurrentByController?: string | null;
    geocodedAddress?: string;
    latitude?: number | null;
    longitude?: number | null;
    organisation?: string;
    abortedFollowUpAction?: string | null;
    hcpsNotified?: string[];
    hcpNotifiedCount?: number;
    expiryDateTime?: string;
}

export enum UpdateJobAction {
    DOUBLEUP_TO_SINGLE = 'DOUBLEUP_TO_SINGLE',
    ABORT_ROUTE = 'ABORT_ROUTE',
}

interface Job extends IJob {}

class Job {
    static IMMUTABLE_PROPERTIES = ['id'];

    static INITIAL_VALUES = {
        practice: '',
        flags: [],
        notes: '',
        systmOneRef: '',
        nominatedHcps: [],
        hcpType: [],
        hcpGrade: [],
        buddyHcpType: [],
        buddyGrade: [],
    };

    constructor(data: IJob) {
        Object.assign(this, data);
    }

    static getFriendlyStatus(jobStatus: JobStatus): string {
        return friendlyStatuses[jobStatus];
    }

    static isOnDemand(jobType: JobType | string): boolean {
        return (
            jobType === JobType.HOME_VISIT ||
            jobType === JobType.TELEPHONE ||
            jobType === JobType.TELEPHONE_BLOCK
        );
    }

    get friendlyStatus(): string {
        return friendlyStatuses[this.jobStatus];
    }

    get requiresAttention(): string[] {
        const requiresAttention = [];
        if (this.jobStatus === JobStatus.HCP_ABORTED) {
            requiresAttention.push('jobStatus');
        }
        return requiresAttention;
    }

    get start(): string | null {
        return this.startDateTime || null;
    }

    get end(): string | null {
        if (this.endDateTime) {
            return this.endDateTime;
        }
        if (this.duration && this.startDateTime) {
            const [hours, minutes] = this.duration.split(':');
            const result = DateTime.fromISO(this.startDateTime).plus({
                hours: parseInt(hours, 10),
                minutes: parseInt(minutes, 10),
            });
            return result.toUTC().toISO();
        }
        return null;
    }
}

export { Job };
